// The owner's feature list on My Subscription (docs/plan-limits-plan.md §7):
// every registry feature, split into "Included in your plan" and "Not in your
// plan" (with a lock). Labels and descriptions come from PLAN_FEATURES — never
// the raw keys. Core features (billing, products, …) are never gated, so they
// are not listed.

import { PLAN_FEATURES, type PlanFeatureKey } from '@bbpos/types';
import { GLASS_PANEL, cn } from '@/components/features/subscriptions/design-tokens';

type PlanFeature = (typeof PLAN_FEATURES)[number];

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="h-3.5 w-3.5"
      role="img"
      aria-label="Not in your plan"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  );
}

function FeatureRow({ feature, included }: { feature: PlanFeature; included: boolean }) {
  return (
    <li
      className={cn(
        'flex items-start gap-3 rounded-xl px-3 py-2.5 transition-all duration-200 hover:shadow-sm',
        included ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-neutral-50 dark:bg-neutral-800/60',
      )}
    >
      <span
        className={cn(
          'mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
          included
            ? 'bg-gradient-to-br from-emerald-500 to-teal-500 text-white'
            : 'bg-neutral-200 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-300',
        )}
      >
        {included ? <CheckIcon /> : <LockIcon />}
      </span>
      <div className="min-w-0">
        <p
          className={cn(
            'text-sm font-semibold',
            included
              ? 'text-neutral-900 dark:text-white'
              : 'text-neutral-600 dark:text-neutral-300',
          )}
        >
          {feature.label}
        </p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">{feature.description}</p>
      </div>
    </li>
  );
}

export function PlanFeaturesSection({ features }: { features: readonly PlanFeatureKey[] }) {
  const included = PLAN_FEATURES.filter((f) => features.includes(f.key));
  const notIncluded = PLAN_FEATURES.filter((f) => !features.includes(f.key));

  return (
    <div className={cn(GLASS_PANEL, 'p-6 mb-6 animate-slide-up')}>
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-md">
          <CheckIcon />
        </div>
        <div>
          <h2 className="text-base font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
            Features
          </h2>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Billing, products, customers and the main reports are always included.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section aria-labelledby="plan-features-included">
          <h3
            id="plan-features-included"
            className="mb-3 text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300"
          >
            Included in your plan
          </h3>
          {included.length > 0 ? (
            <ul className="space-y-2">
              {included.map((f) => (
                <FeatureRow key={f.key} feature={f} included />
              ))}
            </ul>
          ) : (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Your plan has only the basic features.
            </p>
          )}
        </section>

        <section aria-labelledby="plan-features-not-included">
          <h3
            id="plan-features-not-included"
            className="mb-3 text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400"
          >
            Not in your plan
          </h3>
          {notIncluded.length > 0 ? (
            <ul className="space-y-2">
              {notIncluded.map((f) => (
                <FeatureRow key={f.key} feature={f} included={false} />
              ))}
            </ul>
          ) : (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Nothing. Your plan has every feature.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
